import axios from 'axios';
import env from 'variables/config';
import { errorHandler } from './utils';
import { IOrderPreparation, IPreparationInventoryLine } from './orderPreparationTypes';

axios.defaults.baseURL = env.apiUrl;

const config = () => {
    return {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    };
};

// Generate an automatic preparation
export const generatePreparation = async (orderId: string): Promise<IOrderPreparation | null> => {
    const res: any = await axios
        .post('/order-preparations/generate', { orderId }, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Retrieve all preparations
export const getPreparations = async (filters?: {
    status?: string;
    orderId?: string;
    limit?: number;
    skip?: number;
}): Promise<IOrderPreparation[] | null> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.orderId) params.append('orderId', filters.orderId);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.skip) params.append('skip', filters.skip.toString());

    const res: any = await axios
        .get(`/order-preparations?${params.toString()}`, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Retrieve a preparation by ID
export const getPreparationById = async (id: string): Promise<IOrderPreparation | null> => {
    const res: any = await axios
        .get(`/order-preparations/${id}`, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Update a preparation line
export const updatePreparationLine = async (
    preparationId: string,
    productId: string,
    data: {
        inventoryLines?: IPreparationInventoryLine[];
        notes?: string;
    }
): Promise<IOrderPreparation | null> => {
    const res: any = await axios
        .patch(`/order-preparations/${preparationId}/lines/${productId}`, data, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Change the status of a preparation
export const updatePreparationStatus = async (
    preparationId: string,
    status: string
): Promise<IOrderPreparation | null> => {
    const res: any = await axios
        .patch(`/order-preparations/${preparationId}/status`, { status }, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Validate a preparation
export const validatePreparation = async (preparationId: string): Promise<IOrderPreparation | null> => {
    const res: any = await axios
        .post(`/order-preparations/${preparationId}/validate`, {}, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Cancel a preparation
export const cancelPreparation = async (preparationId: string): Promise<IOrderPreparation | null> => {
    const res: any = await axios
        .post(`/order-preparations/${preparationId}/cancel`, {}, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Update the general notes
export const updatePreparationNotes = async (
    preparationId: string,
    notes: string
): Promise<IOrderPreparation | null> => {
    const res: any = await axios
        .patch(`/order-preparations/${preparationId}/notes`, { notes }, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};

// Delete a preparation
export const deletePreparation = async (preparationId: string): Promise<boolean> => {
    const res: any = await axios
        .delete(`/order-preparations/${preparationId}`, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    return !!res;
};

// Get available inventory for a product
export const getProductInventory = async (productId: string): Promise<any[] | null> => {
    const res: any = await axios
        .get(`/order-preparations/product/${productId}/inventory`, config())
        .catch((err: any) => {
            errorHandler(err);
            return null;
        });
    if (!res) return null;
    return res.data.data;
};
